import React, { useState, useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { axios, context as AuthContext } from '../../context/auth'
import Table from '../../styled/table'

const NotificationTable = styled(Table)`
  margin-top: 20px;
  
  th {
    text-align: center;
    font-weight: 600;
  }
  
  td {
    text-align: center;
    vertical-align: middle;
  }
  
  td:first-child {
    text-align: left;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    
    th, td {
      padding: 0.5rem;
    }
  }
`

const CheckboxCell = styled.td`
  input[type="checkbox"] {
    transform: scale(1.2);
    margin: 0;
  }
`

const RowLabel = styled.td`
  font-weight: 500;
  
  .description {
    font-weight: 300;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.bodyText ? `${theme.bodyText}99` : '#666'};
    margin-top: 4px;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
`

const ErrorMessage = styled.div`
  color: #D8000C;
  font-size: 0.9rem;
  margin-top: 5px;
  padding: 0.375rem 0.75rem;
  border-left: 4px solid #FFBABA;
`

const LoadingIndicator = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-top: 5px;
  padding: 0.375rem 0.75rem;
  border-left: 4px solid #DDD;
  display: flex;
  align-items: center;
  gap: 8px;
`

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #666;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

// Notification types organized by action (rows) and content type (columns)
const NOTIFICATION_ACTIONS = [
  {
    key: 'access',
    label: 'Access Granted & Permission Changes',
    description: 'When you are granted access to content or your permissions change',
    songKeys: ['songAdd', 'songPermission'], // Both keys will be updated together
    setlistKeys: ['setlistAdd', 'setlistPermission'] // Both keys will be updated together
  },
  {
    key: 'edit',
    label: 'Edits',
    description: 'When content you have access to is modified',
    songKeys: ['songEdit'],
    setlistKeys: ['setlistEdit']
  },
  {
    key: 'delete',
    label: 'Deletions',
    description: 'When content you have access to is deleted',
    songKeys: ['songDelete'],
    setlistKeys: ['setlistDelete']
  }
]

const NotificationSettings = () => {
  const { user, refreshUser } = useContext(AuthContext)
  const [preferences, setPreferences] = useState(user.notificationPreferences || {})
  const [error, setError] = useState(null)
  const [updating, setUpdating] = useState(false)

  // Create refs for checkboxes to manage indeterminate state
  const checkboxRefs = useRef({})

  // Update local state when user data changes
  useEffect(() => {
    if (user.notificationPreferences) {
      setPreferences(user.notificationPreferences)
    }
  }, [user.notificationPreferences])

  const updatePreferences = useCallback(async (keys, enabled) => {
    if (updating) return // Prevent concurrent updates

    setUpdating(true)
    setError(null)

    // Create update object for multiple keys
    const updateData = {}
    keys.forEach(key => {
      updateData[key] = enabled
    })

    const newPreferences = {
      ...preferences,
      ...updateData
    }

    // Optimistically update local state
    setPreferences(newPreferences)

    try {
      await axios({
        method: 'PUT',
        url: '/api/user/settings/notifications',
        data: updateData
      })

      // Refresh user data to ensure consistency
      refreshUser()
    } catch (err) {
      // Revert local state on error
      setPreferences(preferences)

      // Provide more specific error messages
      let errorMessage = 'Failed to update notification preference. Please try again.'
      if (err.response?.status === 401) {
        errorMessage = 'You are not authorized to update preferences. Please log in again.'
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error occurred. Please try again later.'
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your connection and try again.'
      }

      setError(errorMessage)
      console.error('Error updating notification preference:', err)

      // Clear error after 5 seconds
      setTimeout(() => {
        setError(null)
      }, 5000)
    } finally {
      setUpdating(false)
    }
  }, [preferences, updating, refreshUser])

  const handleToggle = useCallback((keys) => (e) => {
    const enabled = e.target.checked
    updatePreferences(keys, enabled)
  }, [updatePreferences])

  // Helper function to check if all keys for an action are enabled
  const isActionEnabled = useCallback((keys) => {
    return keys.every(key => preferences[key])
  }, [preferences])

  // Helper function to check if any keys for an action are enabled (for indeterminate state)
  const isActionPartiallyEnabled = useCallback((keys) => {
    const enabledCount = keys.filter(key => preferences[key]).length
    return enabledCount > 0 && enabledCount < keys.length
  }, [preferences])

  // Update indeterminate state for checkboxes when preferences change
  useEffect(() => {
    NOTIFICATION_ACTIONS.forEach(({ key, songKeys, setlistKeys }) => {
      const songCheckbox = checkboxRefs.current[`song-${key}`]
      const setlistCheckbox = checkboxRefs.current[`setlist-${key}`]

      if (songCheckbox) {
        songCheckbox.indeterminate = isActionPartiallyEnabled(songKeys)
      }

      if (setlistCheckbox) {
        setlistCheckbox.indeterminate = isActionPartiallyEnabled(setlistKeys)
      }
    })
  }, [preferences, isActionPartiallyEnabled])

  return (
    <div className='NotificationSettings'>
      <h3>Email Notifications</h3>
      <p>Configure which types of notifications you want to receive via email. Notifications are sent hourly when there are updates.</p>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {updating && (
        <LoadingIndicator>
          <Spinner />
          Updating notification preferences...
        </LoadingIndicator>
      )}

      <NotificationTable>
        <thead>
          <tr>
            <th>Notification Type</th>
            <th>Songs</th>
            <th>Setlists</th>
          </tr>
        </thead>
        <tbody>
          {NOTIFICATION_ACTIONS.map(({ key, label, description, songKeys, setlistKeys }) => (
            <tr key={key}>
              <RowLabel>
                <div>{label}</div>
                <div className='description'>{description}</div>
              </RowLabel>
              <CheckboxCell>
                <input
                  type='checkbox'
                  id={`notification-song-${key}`}
                  checked={isActionEnabled(songKeys)}
                  ref={el => { checkboxRefs.current[`song-${key}`] = el }}
                  onChange={handleToggle(songKeys)}
                  disabled={updating}
                  aria-label={`${label} for songs`}
                />
              </CheckboxCell>
              <CheckboxCell>
                <input
                  type='checkbox'
                  id={`notification-setlist-${key}`}
                  checked={isActionEnabled(setlistKeys)}
                  ref={el => { checkboxRefs.current[`setlist-${key}`] = el }}
                  onChange={handleToggle(setlistKeys)}
                  disabled={updating}
                  aria-label={`${label} for setlists`}
                />
              </CheckboxCell>
            </tr>
          ))}
        </tbody>
      </NotificationTable>
    </div>
  )
}

export default NotificationSettings
